import { isCriticalSeverity, isHealthySeverity, isWarningSeverity } from './severityStatuses'

/**
 * Severity color constants for device status indicators
 * Using vibrant, saturated colors that match button gradient colors
 */
export const SEVERITY_COLORS = {
	healthy: '#21c65f',    // Vibrant emerald (matches button)
	warning: '#fbbf24',    // Bright amber (matches button)
	critical: '#ef4444',   // Bright red (for text and button rim)
	default: '#adadad'     // Neutral gray (for waiting/unknown/other severities)
} as const

export type SeverityColorKey = keyof typeof SEVERITY_COLORS

/**
 * Standardized state variants for device tool cards
 * Each variant has consistent colors across all states
 */
export const STATE_VARIANTS = {
	default: {
		id: 'default',
		label: 'Default',
		colorClass: 'gray' as const,
		color: 'bg-mx-gray-500 text-white',
		rimColor: null,
		iconColor: '',
		gradientColor: null
	},
	loading: {
		id: 'loading',
		label: 'Loading',
		colorClass: 'blue' as const,
		color: 'bg-blue-500 text-white',
		rimColor: null, // Uses spinning gradient
		iconColor: '#3b82f6',
		gradientColor: null
	},
	success: {
		id: 'success',
		label: 'Success',
		colorClass: 'green' as const,
		color: 'bg-green-500 text-white',
		rimColor: '#21c65f',
		iconColor: '#21c65f',
		gradientColor: null // Don't show blobs for success
	},
	warning: {
		id: 'warning',
		label: 'Warning',
		colorClass: 'yellow' as const,
		color: 'bg-yellow-500 text-white',
		rimColor: '#fbbf24',
		iconColor: '#fbbf24',
		gradientColor: '#fbbf24'
	},
	error: {
		id: 'error',
		label: 'Error',
		colorClass: 'red' as const,
		color: 'bg-red-500 text-white',
		rimColor: '#ef4444',
		iconColor: '#ef4444',
		gradientColor: '#fb7185'
	},
	unsupported: {
		id: 'unsupported',
		label: 'Unsupported',
		colorClass: 'gray' as const,
		color: 'bg-mx-gray-400 text-white',
		rimColor: null,
		iconColor: '#9ca3af',
		gradientColor: null
	}
} as const

export type StateVariantKey = keyof typeof STATE_VARIANTS
export type StateVariant = typeof STATE_VARIANTS[StateVariantKey]

/**
 * Maps device severity to one of the 5 standardized state variants
 *
 * @param severity - The severity level from the device status response
 * @returns State variant key
 *
 * @example
 * getSeverityStateVariant('ok') // Returns: 'success'
 * getSeverityStateVariant('warning') // Returns: 'warning'
 * getSeverityStateVariant('critical') // Returns: 'error'
 * getSeverityStateVariant('unknown') // Returns: 'initial'
 */
export const getSeverityStateVariant = (severity?: string | null | undefined): StateVariantKey => {
	if (!severity) {
		return 'default'
	}

	// Map healthy severities to success
	if (isHealthySeverity(severity)) {
		return 'success'
	}

	// Map warning severities to warning
	if (isWarningSeverity(severity)) {
		return 'warning'
	}

	// Map critical severities to error
	if (isCriticalSeverity(severity)) {
		return 'error'
	}

	// Unknown/other severities to initial
	return 'default'
}

/**
 * Maps device severity levels to their corresponding display colors
 *
 * @param severity - The severity level from the device status response
 * @returns Hex color code or CSS class for the severity level
 *
 * @example
 * getSeverityColor('ok') // Returns: '#4FAA35' (green)
 * getSeverityColor('warning') // Returns: '#CCAC3B' (yellow)
 * getSeverityColor('critical') // Returns: '#F53A3A' (red)
 * getSeverityColor() // Returns: '#adadad' (default mx-gray-400)
 */
export const getSeverityColor = (severity?: string | null | undefined): string => {
	if (!severity) {
		return SEVERITY_COLORS.default
	}

	// Use centralized severity checks
	if (isHealthySeverity(severity)) {
		return SEVERITY_COLORS.healthy
	}

	if (isWarningSeverity(severity)) {
		return SEVERITY_COLORS.warning
	}

	if (isCriticalSeverity(severity)) {
		return SEVERITY_COLORS.critical
	}

	// Unknown severity
	return SEVERITY_COLORS.default
}
